/**
 * **One cache entry for a client's whole survey history, shared by both surfaces.**
 *
 * Promoted out of `pages/client-detail/api/clientDetailQueries` on 17 September 2026, when
 * `/raporty`'s report dialog became the second reader. Two copies of a query key always become two
 * cache trees for one client: the dialog would fetch a list the Ankiety tab already holds, and
 * „Przejrzana" pressed in one would leave the other showing an unreviewed occurrence. The key
 * descends from `entities/client`'s `clientDetailRoot`, so a write whose blast radius is genuinely
 * "everything about this client" still invalidates one prefix.
 *
 * `pages/client-detail` re-exports both from its own `api/` segment, exactly as it does the five
 * reads that moved to `entities/client`, so its call sites keep reading one file.
 */

import { queryOptions } from '@tanstack/react-query';

import { clientDetailRoot } from '~/entities/client';
import { getCustomerCustomerIdSurveys } from '~/shared/api/generated/endpoints/survey/survey';

export function clientSurveysQueryKey(clientId: number) {
  return [...clientDetailRoot(clientId), 'surveys'] as const;
}

/**
 * The client's surveys, newest first.
 *
 * There is **no "latest" endpoint on this route** — it exposes list and detail only, so the newest
 * completed entry is `surveys[0]` filtered client-side. Recorded here rather than worked around
 * silently: it is a real gap against intake-survey R28, which asks for "the latest at a glance".
 * `/survey/template/latest-submission` answers a different question — the newest submission of ONE
 * FORM across the whole roster — and is read by `lib/useLatestSubmission`.
 */
export function clientSurveysQueryOptions(clientId: number) {
  return queryOptions({
    queryKey: clientSurveysQueryKey(clientId),
    // `includeLeadForm`: a client converted from a public-form lead keeps that lead's answers as a
    // completed questionnaire of their own (public-form R21). The phone never sends it, so it never
    // meets the kind; here it is drawn as a one-off form (`isOneOffKind`), never as the intake.
    queryFn: ({ signal }) =>
      getCustomerCustomerIdSurveys(clientId, { includeLeadForm: true }, undefined, signal),
  });
}
